import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaRadioGroupBoxListField } from '../../components/PerguntaRadioGroupBoxListField'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import {
  ConsultaSeguimentoPergunta14Model,
  ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum,
} from './model-consultaSeguimentoPergunta14'
import {
  calculateResultadoPergunta14,
  conditionToCalculatePergunta14,
  hasPergunta14OlhaSeusOlhosAtLeastTwoSim,
  isPergunta14OlhaSeusOlhosAllFilled,
  isPergunta14OlhaSeusOlhosAllNao,
} from './utils-consultaSeguimentoPergunta14'

interface ConsultaSeguimentoPergunta14FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta14Model>
}

export function ConsultaSeguimentoPergunta14Field(props: Readonly<ConsultaSeguimentoPergunta14FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta14Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta14Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta14,
    calculateResultado: calculateResultadoPergunta14,
  })

  const showPergunta14OlhaSeusOlhos =
    value.PERGUNTA_14_HEADER === SimNaoEnum.NAO || value.PERGUNTA_14_DEU_EXEMPLO_ABAIXO === SimNaoEnum.NAO

  const showPergunta14OlhaSeusOlhosArrow =
    value.PERGUNTA_14_HEADER === SimNaoEnum.SIM && value.PERGUNTA_14_DEU_EXEMPLO_ABAIXO === SimNaoEnum.NAO

  const showPergunta14OlhaSeusOlhosTodosDias =
    isPergunta14OlhaSeusOlhosAllFilled(value.PERGUNTA_14_OLHA_SEUS_OLHOS) &&
    !hasPergunta14OlhaSeusOlhosAtLeastTwoSim(value.PERGUNTA_14_OLHA_SEUS_OLHOS) &&
    !isPergunta14OlhaSeusOlhosAllNao(value.PERGUNTA_14_OLHA_SEUS_OLHOS)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_14}
            title={
              <ConsultaSeguimentoPerguntaHeaderTitle sufix=' olha nos seus olhos quando você está falando, brincando com ele, ou vestindo ele?' />
            }
            status={value.status}
            field={<SimNaoSwitchButtonField name={name.PERGUNTA_14_HEADER} />}
          />
        )}
        <VFlow vSpacing={value.PERGUNTA_14_HEADER === SimNaoEnum.SIM ? 0 : 0.5}>
          {!isEmpty(value.PERGUNTA_14_HEADER) && <ConsultaSeguimentoIndicatorStart />}
          {value.PERGUNTA_14_HEADER === SimNaoEnum.SIM && (
            <VFlow>
              <ConsultaSeguimentoIndicatorHelper
                title='Por favor, dê um exemplo de quando ele olha nos seus olhos.'
                subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar cada um deles individualmente)'
              />
              <VFlow vSpacing={0}>
                <PerguntaRadioGroupBoxListField
                  name={name.PERGUNTA_14_DEU_EXEMPLO_ABAIXO}
                  label='O genitor deu um dos exemplos abaixo?'
                  description='Ele olha nos seus olhos...'
                  items={PERGUNTA_14_OLHA_SEUS_OLHOS_OPTIONS}
                  showItems={value.PERGUNTA_14_DEU_EXEMPLO_ABAIXO !== SimNaoEnum.NAO}
                />
              </VFlow>
            </VFlow>
          )}
          {showPergunta14OlhaSeusOlhos && (
            <VFlow>
              {showPergunta14OlhaSeusOlhosArrow && <ConsultaSeguimentoArrowDown />}
              <VFlow vSpacing={0}>
                <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum>
                  name={name.PERGUNTA_14_OLHA_SEUS_OLHOS}
                  label='Ele olha nos seus olhos...'
                  description='Responda os exemplos até pontuar na Pergunta 14 ou avançar para o próximo fluxo'
                  required
                  options={PERGUNTA_14_OLHA_SEUS_OLHOS_OPTIONS}
                />
                {showPergunta14OlhaSeusOlhosTodosDias && (
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoArrowDown />
                    <PerguntaSimNaoRadioGroupBoxField
                      name={name.PERGUNTA_14_OLHA_SEUS_OLHOS_TODOS_DIAS}
                      label='Seu filho olha nos seus olhos todos os dias?'
                      required
                    />
                    {value.PERGUNTA_14_OLHA_SEUS_OLHOS_TODOS_DIAS === SimNaoEnum.SIM && (
                      <VFlow vSpacing={0}>
                        <ConsultaSeguimentoArrowDown />
                        <PerguntaSimNaoRadioGroupBoxField
                          name={name.PERGUNTA_14_OLHA_SEUS_OLHOS_5_VEZES}
                          label='Nos dias em que vocês estão juntos o dia todo, ele olha no seus olhos pelo menos 5 vezes?'
                          required
                        />
                      </VFlow>
                    )}
                  </VFlow>
                )}
              </VFlow>
            </VFlow>
          )}
        </VFlow>
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: isPergunta14OlhaSeusOlhosAllNao(value.PERGUNTA_14_OLHA_SEUS_OLHOS) ? 'Não para todos:' : undefined,
            PASSOU:
              hasPergunta14OlhaSeusOlhosAtLeastTwoSim(value.PERGUNTA_14_OLHA_SEUS_OLHOS) && showPergunta14OlhaSeusOlhos
                ? 'Sim, para dois ou mais:'
                : undefined,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_14_OLHA_SEUS_OLHOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum>[] = [
  {
    label: 'Quando ele precisa de alguma coisa?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.PRECISA_DE_ALGUMA_COISA,
  },
  {
    label: 'Quando você está brincando com ele?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.BRINCANDO,
  },
  {
    label: 'Durante as alimentações?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.ALIMENTACAO,
  },
  {
    label: 'Durante a troca das fraldas?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.TROCA_FRALDAS,
  },
  {
    label: 'Quando você está lendo uma história para ele?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.LENDO_HISTORIA,
  },
  {
    label: 'Quando você está falando com ele?',
    value: ConsultaSeguimentoPergunta14OlhaSeusOlhosEnum.FALANDO,
  },
]
